import React, { useEffect, useState } from "react";
import LoadingSpinner from "./LoadingSpinner";
import { motion } from "framer-motion";

function parseUtcDate(value) {
  if (!value) return null;

  const valueString = String(value);

  const hasTimezone = /Z$|[+-]\d{2}:\d{2}$/.test(valueString);
  const utcValue = hasTimezone ? valueString : `${valueString}Z`;

  return new Date(utcValue);
}

function truncateUsername(username, maxLength = 20) {
  const value = String(username || "");

  if (value.length <= maxLength) {
    return value;
  }

  return `${value.slice(0, maxLength)}...`;
}

function Countdown({ startDate, endDate }) {
  const [time, setTime] = useState("");

  useEffect(() => {
    const updateCountdown = () => {
      if (!startDate || !endDate) {
        setTime("");
        return;
      }

      const now = new Date();
      const start = parseUtcDate(startDate);
        const end = parseUtcDate(endDate);

        if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
          setTime("");
          return;
        }

      let target;

      if (now < start) {
        target = start;
      } else if (now >= start && now <= end) {
        target = end;
      } else {
        setTime("");
        return;
      }

      const diff = target - now;

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((diff / (1000 * 60)) % 60);
      const seconds = Math.floor((diff / 1000) % 60);

      setTime(`${days}D ${hours}H ${minutes}M ${seconds}S`);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [startDate, endDate]);

  return <span style={{ color: "#fff", fontSize: 14 }}>{time}</span>;
}

function svgFallback(name = "?") {
  const initial = (name?.[0] || "?").toUpperCase();
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64">
      <rect width="100%" height="100%" fill="#444"/>
      <text x="50%" y="50%" dominant-baseline="middle"
            text-anchor="middle" font-size="32"
            fill="#fff" font-family="Arial, sans-serif">${initial}</text>
    </svg>`;
  return `data:image/svg+xml;base64,${btoa(svg)}`;
}

async function fetchJson(url, options = {}) {
  const res = await fetch(url, options);
  const text = await res.text();

  let json = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    throw new Error(`Resposta inválida da API (${res.status})`);
  }

  if (!res.ok) {
    throw new Error(json?.error || json?.message || `HTTP ${res.status}`);
  }

  return json;
}

const statStyle2 = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  fontSize: 21,
  width: 94,
  height: 56,
  backgroundImage: "url('/s2half.png')",
  backgroundSize: "94px 56px",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "center",
  gap: 3,
};

const statGrid = {
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "center",
  gap: 15,
  maxWidth: 500,
    marginTop: -10
};

export default function TournamentModal({
  user,
  setUser,
  onClose,
  onNavigate,
  tournamentEnabled,
  tournamentStartDate,
  tournamentEndDate,
  activeTournamentId,
}) {
  const [entries, setEntries] = useState([]);
  const [tournamentStatusLoading, setTournamentStatusLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [initialLeaderboardLoaded, setInitialLeaderboardLoaded] = useState(false);
  const [tournamentSide, setTournamentSide] = useState("all");
  const [tournamentRankBy] = useState("wins");
  const playerSide = user?.side;
  const rivalSide = playerSide === "good" ? "bad" : "good";
  const [pageSize] = useState(100);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [sideWins, setSideWins] = useState({ good: 0, bad: 0 });
  const [myRank, setMyRank] = useState({
    global_rank: null,
    faction_rank: null,
    wins: 0,
  });

  const [persistedTournamentStartDate, setPersistedTournamentStartDate] = useState(
    tournamentStartDate ?? null
  );
  const [persistedTournamentEndDate, setPersistedTournamentEndDate] = useState(
    tournamentEndDate ?? null
  );

  const [tournamentStatus, setTournamentStatus] = useState({
    joined: false,
    mustJoin: false,
    priceRunes: 0,
    priceSparks: 0,
    activeTournamentId: null,
  });

  const [joiningTournament, setJoiningTournament] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [joinError, setJoinError] = useState("");
  const [showJoinSuccessModal, setShowJoinSuccessModal] = useState(false);
  const [showJoinErrorModal, setShowJoinErrorModal] = useState(false);
  const [joinErrorMessage, setJoinErrorMessage] = useState("");

  useEffect(() => {
    if (tournamentStartDate) {
      setPersistedTournamentStartDate(tournamentStartDate);
    }
    if (tournamentEndDate) {
      setPersistedTournamentEndDate(tournamentEndDate);
    }
  }, [tournamentStartDate, tournamentEndDate]);

  const effectiveTournamentStartDate =
  tournamentStartDate || persistedTournamentStartDate;
const effectiveTournamentEndDate =
  tournamentEndDate || persistedTournamentEndDate;

const now = new Date();
const tournamentStart = parseUtcDate(effectiveTournamentStartDate);
const tournamentEnd = parseUtcDate(effectiveTournamentEndDate);

const tournamentHasNotStarted =
  tournamentStart && now < tournamentStart;

const tournamentIsActive =
  tournamentStart &&
  tournamentEnd &&
  now >= tournamentStart &&
  now <= tournamentEnd;

const tournamentHasEnded =
  tournamentEnd && now > tournamentEnd;

const showEndedState = tournamentHasEnded;

  
  const getRankIcon = (rank) => {
  if (rank === 0) return "/1.png";
  if (rank === 1) return "/2.png";
  if (rank === 2) return "/3.png";
  return `#${rank + 1}`;
};

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  function getJoinErrorMessage(error) {
    const code = String(error || "").trim();

    if (code === "NOT_ENOUGH_SPARKS") {
      return "You do not have enough Sparks to join this tournament.";
    }

    if (code === "NOT_ENOUGH_RUNES") {
      return "You do not have enough Runes to join this tournament.";
    }

    if (code === "NO_ACTIVE_TOURNAMENT") {
      return "There is no active tournament right now.";
    }

    if (code === "TOURNAMENT_JOIN_FAILED") {
      return "Failed to join the tournament. Please try again.";
    }

    return "Something went wrong while trying to join the tournament.";
  }

  useEffect(() => {
    let cancelled = false;

    async function loadTournamentStatus() {
      try {
        if (!cancelled) setTournamentStatusLoading(true);

        const json = await fetchJson(
          `${process.env.REACT_APP_API_URL}/api/battle/tournament/status`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (!cancelled) {
          const nextStartDate = json?.startAt || tournamentStartDate || null;
          const nextEndDate = json?.endAt || tournamentEndDate || null;
          const nextTournamentId = json?.activeTournamentId || activeTournamentId || null;

          setTournamentStatus({
            joined: !!json?.joined,
            mustJoin: !!json?.mustJoin,
            priceRunes: Number(json?.priceRunes || 0),
            priceSparks: Number(json?.priceSparks || 0),
            activeTournamentId: nextTournamentId,
          });

          if (nextStartDate) {
            setPersistedTournamentStartDate(nextStartDate);
          }

          if (nextEndDate) {
            setPersistedTournamentEndDate(nextEndDate);
          }

              if (!nextTournamentId || !nextStartDate) {
  setInitialLeaderboardLoaded(true);
}
        }
      } catch (err) {
        console.error("[tournament/status]", err);

        if (!cancelled) {
          setTournamentStatus({
            joined: false,
            mustJoin: false,
            priceRunes: 0,
            priceSparks: 0,
            activeTournamentId: activeTournamentId || null,
          });

          setInitialLeaderboardLoaded(true);
        }
      } finally {
        if (!cancelled) {
          setTournamentStatusLoading(false);
        }
      }
    }

    loadTournamentStatus();

    return () => {
      cancelled = true;
    };
  }, [activeTournamentId, tournamentEnabled, tournamentStartDate, tournamentEndDate]);

  useEffect(() => {
    if (!effectiveTournamentStartDate || !tournamentStatus.activeTournamentId) {
      return;
    }

    let cancelled = false;

    async function loadMyRank() {
      try {
        const qs = new URLSearchParams({
          from: effectiveTournamentStartDate,
          ...(effectiveTournamentEndDate
            ? { to: effectiveTournamentEndDate }
            : {}),
          tournamentId: tournamentStatus.activeTournamentId,
          rankBy: "wins",
        });

        const json = await fetchJson(
          `${process.env.REACT_APP_API_URL}/api/tournament/my-rank?${qs.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (!cancelled) {
          setMyRank(json || { global_rank: null, faction_rank: null, wins: 0 });
        }
      } catch (err) {
        console.error("[tournament/my-rank]", err);
        if (!cancelled) {
          setMyRank({ global_rank: null, faction_rank: null, wins: 0 });
        }
      }
    }

    loadMyRank();

    return () => {
      cancelled = true;
    };
  }, [
    effectiveTournamentStartDate,
    effectiveTournamentEndDate,
    tournamentStatus.activeTournamentId,
  ]);

  useEffect(() => {
    if (!effectiveTournamentStartDate || !tournamentStatus.activeTournamentId) {
      return;
    }

    let cancelled = false;

    async function load() {
      setLoading(true);

      try {
        const qs = new URLSearchParams({
          from: effectiveTournamentStartDate,
          ...(effectiveTournamentEndDate
            ? { to: effectiveTournamentEndDate }
            : {}),
          tournamentId: tournamentStatus.activeTournamentId,
          side: tournamentSide,
          rankBy: tournamentRankBy,
          limit: "100",
          offset: String((page - 1) * pageSize),
        });

        const json = await fetchJson(
          `${process.env.REACT_APP_API_URL}/api/tournament/stats?${qs.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (cancelled) return;

        const items = json?.items || json || [];
        setEntries(Array.isArray(items) ? items : []);
        setTotal(
          Number.isFinite(json?.total)
            ? json.total
            : Array.isArray(items)
            ? items.length
            : 0
        );

        if (json?.sideWins) {
          setSideWins(json.sideWins);
        } else {
          setSideWins({ good: 0, bad: 0 });
        }
      } catch (err) {
        console.error("[tournament/stats]", err);
        if (!cancelled) {
          setEntries([]);
          setTotal(0);
          setSideWins({ good: 0, bad: 0 });
        }
      } finally {
  if (!cancelled) {
    setLoading(false);
    setInitialLeaderboardLoaded(true);
  }
}
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [
    tournamentSide,
    tournamentRankBy,
    page,
    pageSize,
    effectiveTournamentStartDate,
    effectiveTournamentEndDate,
    tournamentStatus.activeTournamentId,
  ]);

  async function handleJoinTournament() {
    if (joiningTournament) return;

    try {
      setJoiningTournament(true);
      setJoinError("");
      setJoinErrorMessage("");
      setShowJoinErrorModal(false);

      const json = await fetchJson(
        `${process.env.REACT_APP_API_URL}/api/battle/tournament/join`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            tournamentId:
              tournamentStatus.activeTournamentId || activeTournamentId,
          }),
        }
      );

      setTournamentStatus((prev) => ({
        ...prev,
        joined: true,
        activeTournamentId:
          json?.activeTournamentId ||
          prev.activeTournamentId ||
          activeTournamentId ||
          null,
      }));

      if (json?.startAt) {
        setPersistedTournamentStartDate(json.startAt);
      }

      if (json?.endAt) {
        setPersistedTournamentEndDate(json.endAt);
      }

      if (json?.user && typeof setUser === "function") {
        setUser((prev) => ({
          ...prev,
          ...json.user,
        }));
      }

      setShowJoinModal(false);
      setShowJoinSuccessModal(true);
    } catch (err) {
      console.error("[tournament/join]", err);

      const rawMessage = err?.message || "TOURNAMENT_JOIN_FAILED";
      setJoinError(rawMessage);
      setJoinErrorMessage(getJoinErrorMessage(rawMessage));
      setShowJoinModal(false);
      setShowJoinErrorModal(true);
    } finally {
      setJoiningTournament(false);
    }
  }

            const tournamentInitialLoading =
  tournamentStatusLoading || !initialLeaderboardLoaded;

if (tournamentInitialLoading) {
  return (
    <div
      className="shop-container"
      style={{
        paddingTop: 0,
        paddingBottom: 0,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 450,
          minHeight: 430,
          margin: "0 auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <LoadingSpinner
          text="Loading Tournament"
          size={64}
        />
      </div>
    </div>
  );
}

  return (
    <div
      className="shop-container"
      style={{
        fontFamily: '"Fredoka", sans-serif',
        fontWeight: 600,
            paddingBottom: 0
      }}
    >
      <div className="season-modal-page">
        <div className="season-content">
          <div
            style={{
              width: "calc(100% + 20px)",
              height: "120px",
              backgroundImage: "url('/tournamentbg2.png')",
              backgroundSize: "cover",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              position: "relative",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 10,
            marginTop: -10
            }}
          >
            <div className="text-outline-white" style={{ marginBottom: 5, fontSize: 28 }}>
              The Grand Tournament
            </div>

            <div
              style={{
                width: 170,
                minHeight: 58,
                borderRadius: 12,
                backgroundColor: "rgba(0,0,0,0.5)",
                padding: "2px 6px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 3,
              }}
            >
              <span style={{ color: "#FFBD48", fontSize: 15 }}>
  {tournamentHasEnded
    ? "Tournament Status"
    : tournamentHasNotStarted
    ? "Tournament Starts In"
    : "Tournament Ends In"}
</span>

<div style={{ display: "flex", alignItems: "center", gap: 3 }}>
  {tournamentHasEnded ? (
    <div style={{ color: "#fff", fontSize: 12 }}>
      Tournament Ended
    </div>
  ) : (
    <>
      <img src="/newclock.png" alt="" style={{ height: 28 }} />
      <Countdown
        startDate={effectiveTournamentStartDate}
        endDate={effectiveTournamentEndDate}
      />
    </>
  )}
</div>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginBottom: 20,
            }}
          >
            <div
              style={{
                width: "341px",
                height: "61px",
                backgroundImage: "url('/tournament.png')",
                backgroundSize: "contain",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center",
                position: "relative",
              }}
            >
              <div style={{ fontSize: 18, opacity: 0.85 }}>
                <div
                  style={{
                    color: "#FFD100",
                    position: "absolute",
                    top: "0px",
                    left: "0px",
                    height: "61px",
                    lineHeight: "69px",
                    textAlign: "center",
                    width: "50%",
                  }}
                >
                  <b style={{ color: "#fff" }}>{sideWins.good}</b> Wins
                </div>

                <div
                  style={{
                    color: "#FFD100",
                    position: "absolute",
                    top: "0px",
                    right: "0px",
                    height: "61px",
                    lineHeight: "69px",
                    textAlign: "center",
                    width: "50%",
                  }}
                >
                  <b style={{ color: "#fff" }}>{sideWins.bad}</b> Wins
                </div>
              </div>
            </div>
          </div>

          <div style={statGrid}>
            <div style={statStyle2}>
              <div>{myRank.global_rank ? `#${myRank.global_rank}` : "-"}</div>
              <span style={{ fontSize: 12, color: "#FFC97F" }}>Global Rank</span>
            </div>

            <div style={statStyle2}>
              <div>{myRank.wins}</div>
              <span style={{ fontSize: 12, color: "#FFC97F" }}>Total Wins</span>
            </div>

            <div style={statStyle2}>
              <div>{myRank.faction_rank ? `#${myRank.faction_rank}` : "-"}</div>
              <span style={{ fontSize: 12, color: "#FFC97F" }}>Faction Rank</span>
            </div>

            {tournamentEnabled && tournamentStatusLoading && (
              <div
                style={{
                  marginTop: 10,
                  marginBottom: 20,
                  width: "280px",
                  height: "56px",
                }}
              />
            )}

{tournamentIsActive &&
  Boolean(tournamentStatus.activeTournamentId) &&
  !tournamentStatusLoading &&
  tournamentStatus.joined && (
                <div
                  onClick={() => onNavigate("arenas")}
              className="game-button green-button"
                  style={{
                    marginTop: 10,
                    marginBottom: 0,
                    cursor: "pointer",
                  }}
                >
                  ENTER THE ARENA
                </div>
              )}

          {tournamentIsActive &&
  Boolean(tournamentStatus.activeTournamentId) &&
  !tournamentStatusLoading &&
  !tournamentStatus.joined && (
                <div
                  onClick={() => {
                    setJoinError("");
                    setShowJoinModal(true);
                  }}
              className="game-button green-button"
                  style={{
                    marginTop: 10,
                    marginBottom: 0,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 10,
                  }}
                >
                  <span>
                    {tournamentStatus.priceRunes <= 0 && tournamentStatus.priceSparks <= 0
                      ? "JOIN FOR FREE"
                      : "JOIN NOW"}
                  </span>

                  {(tournamentStatus.priceRunes > 0 || tournamentStatus.priceSparks > 0) && (
                    <>
                      <img
                        src={
                          tournamentStatus.priceSparks > 0
                            ? "/sparks.png"
                            : "/runes.png"
                        }
                        alt=""
                        style={{
                          width: 26,
                          height: 26,
                          objectFit: "contain",
                        }}
                      />

                      <span>
                        {tournamentStatus.priceRunes > 0 && tournamentStatus.priceSparks > 0
                          ? `${tournamentStatus.priceRunes}+${tournamentStatus.priceSparks}`
                          : tournamentStatus.priceRunes > 0
                          ? tournamentStatus.priceRunes
                          : tournamentStatus.priceSparks}
                      </span>
                    </>
                  )}
                </div>
              )}

                          <div style={{marginLeft: -10, marginRight: -10, width: "calc(100% + 20px)"}}>
            <div className="modal_title">Leaderboard</div>
                          <div className="modal_body modal_body_2">
            <div
  style={{
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    width: "100%",
    gap: 8,
    alignItems: "stretch",
  }}
>
  <button
    type="button"
    onClick={() => {
      setTournamentSide("all");
      setPage(1);
    }}
    style={{
      width: "100%",
      height: 46,
      padding: "0 6px",
      margin: 0,
      boxSizing: "border-box",

      background:
        tournamentSide === "all"
          ? "rgba(112, 61, 27, 0.96)"
          : "rgba(95, 49, 18, 0.76)",

      border:
        tournamentSide === "all"
          ? "2px solid rgba(135, 78, 36, 0.98)"
          : "2px solid rgba(70, 32, 10, 0.95)",

      borderRadius: 7,

      boxShadow:
        tournamentSide === "all"
          ? "inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -2px 0 rgba(0,0,0,0.24), 0 2px 0 rgba(0,0,0,0.24)"
          : "inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -2px 0 rgba(0,0,0,0.18), 0 2px 0 rgba(0,0,0,0.18)",

      color: "#fff",
      fontSize: 12,
      lineHeight: "42px",
      fontWeight: 600,
      textAlign: "center",

      textShadow:
        "-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000, 0 1px 1px rgba(0,0,0,.75)",

      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",

      cursor: "pointer",
      appearance: "none",
      WebkitAppearance: "none",
      opacity: tournamentSide === "all" ? 1 : 0.88,
    }}
  >
    Global Rank
  </button>

  <button
    type="button"
    onClick={() => {
      setTournamentSide(playerSide);
      setPage(1);
    }}
    style={{
      width: "100%",
      height: 46,
      padding: "0 6px",
      margin: 0,
      boxSizing: "border-box",

      background:
        tournamentSide === playerSide
          ? "rgba(112, 61, 27, 0.96)"
          : "rgba(95, 49, 18, 0.76)",

      border:
        tournamentSide === playerSide
          ? "2px solid rgba(135, 78, 36, 0.98)"
          : "2px solid rgba(70, 32, 10, 0.95)",

      borderRadius: 7,

      boxShadow:
        tournamentSide === playerSide
          ? "inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -2px 0 rgba(0,0,0,0.24), 0 2px 0 rgba(0,0,0,0.24)"
          : "inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -2px 0 rgba(0,0,0,0.18), 0 2px 0 rgba(0,0,0,0.18)",

      color: "#fff",
      fontSize: 12,
      lineHeight: "42px",
      fontWeight: 600,
      textAlign: "center",

      textShadow:
        "-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000, 0 1px 1px rgba(0,0,0,.75)",

      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",

      cursor: "pointer",
      appearance: "none",
      WebkitAppearance: "none",
      opacity: tournamentSide === playerSide ? 1 : 0.88,
    }}
  >
    Your Faction
  </button>

  <button
    type="button"
    onClick={() => {
      setTournamentSide(rivalSide);
      setPage(1);
    }}
    style={{
      width: "100%",
      height: 46,
      padding: "0 6px",
      margin: 0,
      boxSizing: "border-box",

      background:
        tournamentSide === rivalSide
          ? "rgba(112, 61, 27, 0.96)"
          : "rgba(95, 49, 18, 0.76)",

      border:
        tournamentSide === rivalSide
          ? "2px solid rgba(135, 78, 36, 0.98)"
          : "2px solid rgba(70, 32, 10, 0.95)",

      borderRadius: 7,

      boxShadow:
        tournamentSide === rivalSide
          ? "inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -2px 0 rgba(0,0,0,0.24), 0 2px 0 rgba(0,0,0,0.24)"
          : "inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -2px 0 rgba(0,0,0,0.18), 0 2px 0 rgba(0,0,0,0.18)",

      color: "#fff",
      fontSize: 12,
      lineHeight: "42px",
      fontWeight: 600,
      textAlign: "center",

      textShadow:
        "-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000, 0 1px 1px rgba(0,0,0,.75)",

      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",

      cursor: "pointer",
      appearance: "none",
      WebkitAppearance: "none",
      opacity: tournamentSide === rivalSide ? 1 : 0.88,
    }}
  >
    Rival Faction
  </button>
</div>
          

          <div style={{ width: "100%", marginTop: 15, marginBottom: 10, float: "left" }}>
            {loading && entries.length === 0 ? (
              <div style={{ color: "#fff", textAlign: "center", opacity: 0.8 }}>
                Loading leaderboard...
              </div>
            ) : entries.length === 0 ? (
              <div
                style={{
                  color: "#fff",
                  textAlign: "center",
                  padding: "0px 12px",
                  fontSize: "14px",
                  textShadow: "1px 1px #000",
                }}
              >
                No players have joined the tournament yet.
                <br />
                Be the first to join!
              </div>
            ) : (
              entries.map((entry, idx) => {
                const offset = (page - 1) * pageSize;
                const globalIdx = offset + idx;
                const count = entry.wins;
                const rankLabel = getRankIcon(globalIdx);

                return (
                  <motion.div
                    key={entry.id ?? idx}
                    className="task-card"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: idx * 0.05 }}
                    style={{
                      width: "100%",
                      paddingLeft: "18px",
                      paddingRight: "21px",
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      padding: "10px",
                    }}
                  >
                    <span
                      style={{
                        minWidth: 32,
                        width: 32,
                        height: 32,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        fontSize: 20,
                        color: "#fff",
                        flex: "0 0 32px",
                      }}
                    >
                      {globalIdx <= 2 ? (
                        <img
                          src={rankLabel}
                          alt={`Rank ${globalIdx + 1}`}
                          draggable={false}
                          style={{
                            width: 32,
                            height: 32,
                            objectFit: "contain",
                            display: "block",
                          }}
                        />
                      ) : (
                        rankLabel
                      )}
                    </span>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      <b
                        style={{
                          lineHeight: "32px",
                          borderRadius: "8px",
                          display: "inline-block",
                          backgroundColor: "#000",
                          width: "32px",
                          height: "32px",
                          overflow: "hidden",
                          position: "relative",
                          flex: "0 0 32px",
                        }}
                      >
                        <img
                          src={entry.avatar || entry.avatarUrl || svgFallback(entry.username)}
                          alt=""
                          style={{
                            border: entry?.early_bird ? "1px solid #FFD700" : "0px",
                            borderRadius: "8px",
                            boxSizing: "border-box",
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                          }}
                        />

                        {entry?.early_bird && (
                          <img
                            src="/whitelist.png"
                            style={{
                              position: "absolute",
                              bottom: "1px",
                              right: "1px",
                              width: "15px",
                              height: "15px",
                            }}
                          />
                        )}

                        {entry?.gamepass_active && (
                          <img
                            src="/passicon.png"
                            style={{
                              position: "absolute",
                              bottom: "1px",
                              left: "1px",
                              width: "15px",
                              height: "15px",
                            }}
                          />
                        )}
                      </b>

                      <span
                        style={{
                          fontSize: "16px",
                          color: "#ffcc62",
                          textShadow: "1px 1px #000",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {truncateUsername(entry.username, 20)}
                      </span>
                    </div>

                    <div
                      style={{
                        fontSize: "16px",
                        backgroundImage: "url('/v2/trophy.png')",
                        backgroundSize: "21px 21px",
                        paddingLeft: "26px",
                        paddingRight: "10px",
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "left center",
                        color: "#fff",
                        textShadow: "1px 1px #000",
                      }}
                    >
                      {count}
                    </div>
                  </motion.div>
                );
              })
            )}
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 5,
              width: "100%",
              padding: "0 24px",
              boxSizing: "border-box",
              marginTop: 30,
              marginBottom: 0,
            }}
          >

            <button
  disabled={page >= totalPages}
  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
  style={{
    backgroundColor: "transparent",
    border: "none",
    padding: 0,
    width: 44,
    height: 44,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    cursor: page >= totalPages ? "not-allowed" : "pointer",
    boxShadow: page >= totalPages ? "none" : "0 0 6px #ffcc62",
  }}
>
  <img
    src="/v2/left.png"
    alt="Previous"
    style={{
      width: 44,
      height: 44,
      objectFit: "contain",
      display: "block",
      pointerEvents: "none",
    }}
  />
</button>

            <div
              style={{
               height: 42,
  lineHeight: "38px",
  borderRadius: 8,
  background: "rgba(0,0,0,0.45)",
  border: "2px solid #7f5031",
  color: "#fff",
  fontSize: 14,
  textTransform: "uppercase",
  fontWeight: 600,
  textAlign: "center",
  textShadow: "1px 1px #000",
  boxSizing: "border-box",
  whiteSpace: "nowrap",
  fontFamily: '"Fredoka", sans-serif',
  padding: "0px 15px",
              }}
            >
              Page {page} / {totalPages}
            </div>

            <button
  disabled={page >= totalPages}
  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
  style={{
    backgroundColor: "transparent",
    border: "none",
    padding: 0,
    width: 44,
    height: 44,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    cursor: page >= totalPages ? "not-allowed" : "pointer",
    boxShadow: page >= totalPages ? "none" : "0 0 6px #ffcc62",
  }}
>
  <img
    src="/v2/right.png"
    alt="Next"
    style={{
      width: 44,
      height: 44,
      objectFit: "contain",
      display: "block",
      pointerEvents: "none",
    }}
  />
</button>

          </div>
        </div>
      </div></div>

                          </div></div>

      {showJoinModal && (
        <div className="modal_box">
                          <div className="modal_title">Confirm<button onClick={() => {
                  if (joiningTournament) return;
                  setShowJoinModal(false);
                  setJoinError("");
                }} className="modal_close"/></div>
          <motion.div
            className="modal_body" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}         
          >
                          <div className="modal_inside">
            <img
              src="/finger.png"
              alt="Join Tournament"
              style={{ height: "80px", marginBottom: "10px" }}
            />

            <div className="shadow2" style={{ fontWeight: 600, fontSize: 28}}>
              Join Tournament?
            </div>

            <div className="shadow1"
              style={{
                fontWeight: 600,
                fontSize: "15px",
                          marginTop: 10,
                          marginBottom: 10
              }}
            >
              Join this tournament to unlock battles.
                          </div>
              
              <div className="info">
                Cost:{" "}
                {tournamentStatus.priceRunes > 0 && (
                  <>{tournamentStatus.priceRunes} Runes</>
                )}
                {tournamentStatus.priceRunes > 0 &&
                  tournamentStatus.priceSparks > 0 &&
                  " + "}
                {tournamentStatus.priceSparks > 0 && (
                  <>{tournamentStatus.priceSparks} Sparks</>
                )}
                {tournamentStatus.priceRunes <= 0 &&
                  tournamentStatus.priceSparks <= 0 &&
                  "Free"}
              </div>
            

                          </div>

            <div className="modal_footer">
              <button className="game-button green-button" style={{marginRight: 10}} onClick={handleJoinTournament} disabled={joiningTournament}>
                {joiningTournament ? "Joining..." : "Confirm"}
              </button>

              <button className="game-button red-button"
                onClick={() => {
                  if (joiningTournament) return;
                  setShowJoinModal(false);
                  setJoinError("");
                }}
              >
                CANCEL
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {showJoinErrorModal && (
        <div className="modal_box">
                          <div className="modal_title">OOPS!<button onClick={() => {
                  setShowJoinErrorModal(false);
                  setJoinError("");
                  setJoinErrorMessage("");
                }} className="modal_close"/></div>
          <motion.div
            className="modal_body" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}
          >
                          <div className="modal_inside">
            <img
              src="/finger.png"
              alt="Error"
              style={{
                height: "80px",
                marginBottom: "10px",
                transform: "rotate(180deg)",
              }}
            />

            <div className="shadow2" style={{fontWeight: 600, fontSize: 28, marginBottom: "10px" }}>
              Unable to Join
            </div>

            <div className="info"
              style={{
                fontSize: "18px",
              }}
            >
              {joinErrorMessage}
            </div>
                          </div>

            <div className="modal_footer">
              <button className="game-button red-button"
                onClick={() => {
                  setShowJoinErrorModal(false);
                  setJoinError("");
                  setJoinErrorMessage("");
                }}
              >
                CLOSE
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {showJoinSuccessModal && (
        <div className="modal_box">
                          <div className="modal_title">Success<button onClick={() => {
                  setShowJoinSuccessModal(false);
                }} className="modal_close"/></div>
          <motion.div
            className="modal_body" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}
          >
                          <div className="modal_inside">
            <img
              src="/finger.png"
              alt="Success"
              style={{
                height: "80px",
                marginBottom: "10px",
              }}
            />

            <div className="shadow2" style={{fontWeight: 600, fontSize: 28, marginBottom: "10px" }}>
              Tournament Joined!
            </div>

            <div className="info"
              style={{
                textShadow: "1px 1px #000",
                fontSize: "20px",
                color: "#ddd",
                lineHeight: "26px",
              }}
            >
              You are now part of the tournament.
              <br />
              <span style={{ color: "#ffbb01", fontWeight: "bold" }}>
                Go Win Some Battles!
              </span>
            </div>
            </div>

            <div className="modal_footer">
              <button
                          className="game-button green-button"
                onClick={() => {
                  setShowJoinSuccessModal(false);
                }}
              >
                LET'S GO!
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}